import { validationIsEqual } from '../status';
import {
    validateEmail,
    validateName,
    validateContactNumber,
    validateSingleSelection,
    validateAlphaNumericString,
    validateNumericString,
    validateDateOfBirth,
    validatePassportNumber,
} from '@om/validation';
import {
    NATIONALITY_TYPE_SET_VALUE,
    setNationalityTypeValid,
    ID_NUMBER_SET_VALUE,
    setIdNumberValid,
    PASSPORT_NUMBER_SET_VALUE,
    setPassportNumberValid,
    TIN_NUMBER_SET_VALUE,
    setTinNumberValid,
    PROFESSION_SET_VALUE,
    setProfessionValid,
    PHONE_NUMBER_SET_VALUE,
    setPhoneNumberValid,
    OFFICE_NUMBER_SET_VALUE,
    setOfficeNumberValid,
    EMAIL_SET_VALUE,
    setEmailValid,
    POSTAL_ADDRESS_SET_VALUE,
    setPostalAddressValid,
    TOWN_SET_VALUE,
    setTownValid,
    BANK_NAME_SET_VALUE,
    setBankNameValid,
    BANK_ACCOUNT_NUMBER_SET_VALUE,
    setBankAccountNumberValid,
    BANK_HOLDER_NAME_SET_VALUE,
    setBankHolderNameValid,
    BANK_BRANCH_SET_VALUE,
    setBankBranchValid,
    BANK_ACCOUNT_CURRENCY_SET_VALUE,
    setBankAccountCurrencyValid,
    FULL_NAME_SET_VALUE,
    setFullNameValid,
    EMERGENCY_CONTACT_FULL_NAME_VALUE,
    setEmergencyContactFullNameValid,
    EMERGENCY_CONTACT_EMAIL_ADDRESS_VALUE,
    setEmergencyContactEmailAddressValid,
    EMERGENCY_CONTACT_PHONE_NUMBER_VALUE,
    setEmergencyContactPhoneNumberValid,
    DOB_SET_VALUE,
    setDOBValid,
} from '../../../actions/travelSs/travelDetails';
import { getSelectedCover } from '../../../selectors/travelSs/quote';
import { getCurrentTravellerIndex } from '../../../selectors/travelSs/travelDetails';

let prevValidationAction = null;

export const travelDetailsValidation = (store, next, action) => {
    const state = store.getState();

    function validatePassport(passport) {
        const passportRegex = /^[A-Z0-9]{8,20}$/;
        return passportRegex.test(passport);
    }

    function validatePIN(input) {
        // Check if the input is empty
        if (!input || input.trim() === '') {
            return 'Personal Identification Number cannot be empty';
        }
    
        // Regular expression for one letter followed by exactly 13 digits
        const regex = /^[A-Za-z]\d{13}$/;
    
        // Validate the input against the regular expression
        if (!regex.test(input)) {
            return 'Personal Identification Number input format. Must be one letter followed by 13 digits.';
        }
    
        // If input is valid, return null
        return null;
    }

    const gatsbyStack = process.env.GATSBY_COUNTRY_SELECTOR;

    const selectedCover = getSelectedCover(state);
    const currentTravellerIndex = getCurrentTravellerIndex(state);

    let minAge = currentTravellerIndex === 0 ? 5 : 0;
    let maxAge = 75;

    if (selectedCover.value === 'Student') {
        maxAge = 35;
    }

    if (selectedCover.value === 'Senior') {
        minAge = 76;
        maxAge = 85;
    }

    const validateUgNin = (nin) => {
        const regex = /^[A-Z]{2}[A-Z0-9]{12}$/;
        return regex.test(nin);
    };

    const validateString = (value, min, subject) => {
        const validators = [validateName, validateNumericString, validateAlphaNumericString];

        let customError = null;

        for (const validate of validators) {
            customError = validate(value, { min, subject });
            if (!customError) break;
        }

        return customError;
    };

    function validatePhoneNumber(phoneNumber, required = true) {
        // If not required and the phone number is empty or null, return null
        if (!required && (phoneNumber === "" || phoneNumber === null || phoneNumber === undefined)) {
            return null;
        }
    
        const regex = /^0\d{9}$/;
        
        // If it's required or there's input, validate the phone number format
        if (regex.test(phoneNumber)) {
            return null; // valid phone number
        }
        
        return "Phone number must start with 0 and have exactly 10 digits."; // error message
    }
    
    

    const validationAction = (() => {
        let error;
        switch (action.type) {
            case NATIONALITY_TYPE_SET_VALUE:
                error = validateSingleSelection(action.value, {
                    subject: 'Nationality Type',
                });
                return setNationalityTypeValid(error);

            case ID_NUMBER_SET_VALUE:
                error = validatePIN(action.value);
                return setIdNumberValid(error);

            case PASSPORT_NUMBER_SET_VALUE:
                const passportIsValid = validatePassport(action.value);
                error = passportIsValid ? null : 'Enter valid passport number';

                return setPassportNumberValid(error);

            case TIN_NUMBER_SET_VALUE:
                error = validateNumericString(action.value, { min: 10, max: 10 });
                return setTinNumberValid(error);

            case PROFESSION_SET_VALUE:
                error = validateName(action.value, {
                    min: 2,
                    max: 48,
                    subject: 'Profession',
                });
                return setProfessionValid(error);

            case PHONE_NUMBER_SET_VALUE:
                
                error = validatePhoneNumber(action.value, true);
                return setPhoneNumberValid(error);

            case OFFICE_NUMBER_SET_VALUE:
                error = validatePhoneNumber(action.value, false);
                return setOfficeNumberValid(error);

            case EMAIL_SET_VALUE:
                error = validateEmail(action.value, {
                    min: 6,
                    max: 200,
                    subject: 'Email address',
                    required: true,
                });
                return setEmailValid(error);

            case POSTAL_ADDRESS_SET_VALUE:
                error = validateString(action.value, { min: 3, subject: 'Postal Address' });
                return setPostalAddressValid(error);

            case TOWN_SET_VALUE:
                error = validateName(action.value, {
                    min: 2,
                    max: 48,
                    subject: 'Town',
                });
                return setTownValid(error);

            case BANK_NAME_SET_VALUE:
                error = validateName(action.value, {
                    min: 2,
                    max: 48,
                    subject: 'Town',
                });
                return setBankNameValid(error);

            case BANK_ACCOUNT_NUMBER_SET_VALUE:
                error = validateString(action.value, { min: 3, subject: 'Bank Account Number' });
                return setBankAccountNumberValid(error);

            case BANK_HOLDER_NAME_SET_VALUE:
                error = validateName(action.value, {
                    min: 2,
                    max: 48,
                    subject: 'Town',
                });
                return setBankHolderNameValid(error);

            case BANK_BRANCH_SET_VALUE:
                error = validateString(action.value, { min: 3, subject: 'Bank Branch' });
                return setBankBranchValid(error);

            case BANK_ACCOUNT_CURRENCY_SET_VALUE:
                error = validateSingleSelection(action.value, {
                    subjet: 'Currency',
                });
                return setBankAccountCurrencyValid(error);

            case FULL_NAME_SET_VALUE:
                error = error = validateName(action.value, {
                    min: 2,
                    max: 48,
                    subject: 'Full Name',
                });
                return setFullNameValid(error);

            case DOB_SET_VALUE:
                error = validateDateOfBirth(action.value, { min: minAge, max: maxAge, subject: 'Date of birth' });

                return setDOBValid(error);

            case EMERGENCY_CONTACT_FULL_NAME_VALUE:
                error = error = validateName(action.value, {
                    min: 2,
                    max: 48,
                    subject: 'Full Name',
                });
                return setEmergencyContactFullNameValid(error);

            case EMERGENCY_CONTACT_EMAIL_ADDRESS_VALUE:
                error = validateEmail(action.value, {
                    min: 6,
                    max: 200,
                    subject: 'Email address',
                    required: true,
                });
                return setEmergencyContactEmailAddressValid(error);

            case EMERGENCY_CONTACT_PHONE_NUMBER_VALUE:
                error = validatePhoneNumber(action.value, true);
                return setEmergencyContactPhoneNumberValid(error);

            default:
                return null;
        }
    })();

    if (!validationAction || validationIsEqual(validationAction, prevValidationAction)) {
        return next(action);
    }

    store.dispatch(validationAction);
    prevValidationAction = validationAction;
    return next(action);
};
